import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Video, 
  FileText, 
  Activity, 
  ClipboardCheck, 
  User, 
  Info,
  Calendar,
  Mail,
  Phone,
  Loader2,
  Stethoscope,
  Sparkles
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { calculateAge } from "../utils/utils";
import InitialsCircle from "../components/List/InitialsCircle";
import SubjectiveContent from "./patient/SubjectiveContent";
import ObjectiveContent from "./patient/ObjectiveContent";
import AssessmentContent from "./patient/AssessmentContent";
import PlanContent from "./patient/PlanContent";
import TeleconsultHistory from "./patient/TeleconsultHistory";
import { AmbientAssistant } from "../components/AmbientAI/AmbientAssistant";
import { BASE_URL } from "../components/interface/Config";
import { apiHandler } from "../services/authService";

const SafeAnimatePresence = AnimatePresence as any;

interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  gender: "Male" | "Female" | "Other";
  dob: string;
  blood_group: string;
  address: {
    city: string;
    state: string;
    country: string;
    zipcode: string;
    address1: string;
    address2: string;
  };
  hospital_name: string;
  profile_image: string | null;
}

const PatientDetails: React.FC = () => {
  const navigate = useNavigate();
  const { refNumber } = useParams();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(() => {
    return window.location.pathname.includes("HealthVitals") ? "objective" : "Teleconsult";
  });
  const [isTabTransitioning, setIsTabTransitioning] = useState(false);

  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setLoading(true);
        const url = `${BASE_URL}/resource/users/${refNumber}`;
        const response = await apiHandler('', 'GET', {}, url);
        if (response?.status === 200) {
          setPatient(response.data.data.users);
        }
      } catch (error) {
        console.error("Error fetching patient data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (refNumber) fetchPatientData();
  }, [refNumber]);

  const handleTabChange = (tab: string) => {
    setIsTabTransitioning(true);
    setSelectedTab(tab);
    setTimeout(() => setIsTabTransitioning(false), 300);
  };

  const tabs = [
    { id: "Teleconsult", label: "Consult History", icon: <Video size={18} /> },
    { id: "subjective", label: "Subjective", icon: <FileText size={18} /> },
    { id: "objective", label: "Objective", icon: <Activity size={18} /> },
    { id: "assessment", label: "Assessment", icon: <Stethoscope size={18} /> },
    { id: "plan", label: "Care Plan", icon: <ClipboardCheck size={18} /> },
  ];

  if (loading && !patient) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
        <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
        <p className="text-slate-500 font-bold tracking-widest uppercase text-xs">Accessing Patient File...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 font-primary flex flex-col overflow-x-hidden">
      {/* Premium Clinical Header */}
      <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-4 flex flex-col xl:flex-row gap-4 xl:items-center justify-between shadow-sm z-20">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-2xl bg-slate-100 text-slate-600 hover:bg-primary hover:text-white transition-all shadow-sm border border-slate-200 shrink-0"
          >
            <ArrowLeft size={20} />
          </button>

          <div className="flex items-center gap-4">
             <div className="relative shrink-0">
                <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center text-white font-black text-lg shadow-lg shadow-primary/20">
                   {patient?.first_name?.[0]}{patient?.last_name?.[0]}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full shadow-sm" />
             </div>
             <div className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <h1 className="text-base sm:text-lg font-black text-slate-900 tracking-tight leading-none truncate">
                    {patient?.first_name} {patient?.last_name}
                  </h1>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-black text-[9px] uppercase tracking-widest whitespace-nowrap">
                    {calculateAge(patient?.dob || "")} Yrs • {patient?.gender}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-400 text-[10px] font-bold">
                   <span className="flex items-center gap-1"><Phone size={10} /> {patient?.mobile}</span>
                   <span className="flex items-center gap-1"><Mail size={10} /> {patient?.email}</span>
                </div>
             </div>
          </div>
        </div>

        {/* Clinical Tabs */}
        <nav className="flex items-center gap-1 bg-slate-100/80 p-1.5 rounded-2xl border border-slate-200 overflow-x-auto scrollbar-hide w-full xl:w-auto shrink-0 max-w-full">
           {tabs.map((tab) => (
             <button
               key={tab.id}
               onClick={() => handleTabChange(tab.id)}
               className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-all duration-200 whitespace-nowrap shrink-0 ${
                 selectedTab === tab.id 
                   ? "bg-white text-primary shadow-sm font-black" 
                   : "text-slate-500 hover:text-slate-700 font-bold"
               }`}
             >
               <span className={selectedTab === tab.id ? "text-primary" : "text-slate-400"}>
                 {tab.icon}
               </span>
               <span className="text-xs tracking-tight">{tab.label}</span>
             </button>
           ))}
        </nav>
      </header>

      {/* Workspace Area */}
      <main className="flex-grow overflow-y-auto p-3 sm:p-4 md:p-6 scrollbar-hide relative space-y-6">
        <SafeAnimatePresence mode="wait">
          {!isTabTransitioning && (
            <motion.div
              key={selectedTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              <div className="bg-white rounded-2xl sm:rounded-[2.5rem] border border-slate-100 shadow-sm min-h-full overflow-hidden">
                {selectedTab === "Teleconsult" && refNumber && (
                  <div className="p-4 sm:p-6"><TeleconsultHistory patientId={refNumber} /></div>
                )}
                {selectedTab === "subjective" && (
                  <div className="p-4 sm:p-6"><SubjectiveContent id={refNumber || ""} gender={patient?.gender || "Other"} /></div>
                )}
                {selectedTab === "objective" && refNumber && (
                  <div className="p-4 sm:p-6"><ObjectiveContent id={refNumber} /></div>
                )}
                {selectedTab === "assessment" && refNumber && (
                  <div className="p-4 sm:p-6"><AssessmentContent id={refNumber} /></div>
                )}
                {selectedTab === "plan" && refNumber && (
                  <div className="p-4 sm:p-6"><PlanContent id={refNumber} /></div>
                )}
              </div>
            </motion.div>
          )}
        </SafeAnimatePresence>

        {/* Quick Actions - Mini Fab Stack */}
        <div className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 flex flex-col gap-3 z-[100]">
           <button 
              onClick={() => {
                window.dispatchEvent(new CustomEvent('open-ambient-ai', { 
                  detail: { patientId: refNumber, patientName: `${patient?.first_name} ${patient?.last_name}` } 
                }));
              }}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl sm:rounded-[1.5rem] bg-gradient-to-tr from-violet-600 to-indigo-600 text-white shadow-2xl shadow-indigo-500/20 flex items-center justify-center hover:scale-110 active:scale-95 transition-all group"
           >
              <Sparkles size={20} className="animate-pulse" />
              <span className="absolute right-full mr-3 sm:mr-4 px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">Ambient AI</span>
           </button>

           <button className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl sm:rounded-[1.5rem] bg-slate-900 text-white shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all group">
              <Video size={20} />
              <span className="absolute right-full mr-3 sm:mr-4 px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">Start Teleconsult</span>
           </button>
        </div>
      </main>

      {/* Decorative Branding */}
      <div className="absolute top-[20%] right-0 w-[300px] sm:w-[400px] md:w-[500px] h-[300px] sm:h-[400px] md:h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none translate-x-1/2" />

      {/* Globally Accessible Ambient AI Assistant Floating Card */}
      {refNumber && <AmbientAssistant patientId={refNumber} hideGlobalTrigger={true} />}
    </div>
  );
};

export default PatientDetails;
